import { z } from 'zod';

import type { AdPlatform } from '../enums.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLATFORM OPERASYON KATALOĞU (POK) — SÖZLEŞME
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bir reklam platformunda yapılabilen HER işlem burada tipli bir operasyon:
 * kimliği, parametre şeması (Zod), harcama etkisi, geri alınabilirliği ve
 * platformun istediği erişim. Uygulaması (gerçek API çağrısı) API tarafında,
 * bağlayıcının `ops/` dizininde (`OpHandler`).
 *
 * Aynı katalog beş tüketiciye gidiyor ve bu yüzden TEK:
 *   · sohbet ajanı      → her operasyon bir araç (`defer_loading` + araç arama)
 *   · arka plan operatörü → aynı araçlar
 *   · MCP / geliştirici API → `list_operations` / `run_operation`
 *   · otomatik pilot politikası → `impact` + `reversible` + bütçe hedefi
 *   · arayüz             → kart başlığı (`title`), risk rozeti
 *
 * Ayrı bir liste yazmak yasak (hafıza: sözleşme-vs-gerçeklik): tüketen taraf
 * `PLATFORM_OPS`u OKUR.
 *
 * Ayrıntı ve gerekçe: /var/www/vhosts/mentorivo.com/OTONOM-OPERATOR-PLANI.md
 */

// ═══════════════════════════════════════════════════════════════════════════
// Etki — otomatik pilotun karar girdisi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Operasyonun HARCAMA üzerindeki etkisi.
 *
 *   NONE      okuma, ad/metin değişikliği, hedefleme DARALTMA, negatif kelime
 *   DECREASE  duraklatma, bütçe/teklif düşürme, kaldırma
 *   INCREASE  bütçe/teklif artırma, hedefleme GENİŞLETME, mevcut bir varlığı
 *             yeniden etkinleştirme
 *   NEW_SPEND yeni harcama yolu açan her şey: kampanya oluşturma, yeni
 *             varlığı AKTİF yapma
 *
 * Emin değilsen yukarı yuvarla: yanlış sınıflanmış bir INCREASE, otomatik
 * pilotta onaysız para harcamak demek; yanlış sınıflanmış bir NONE ise
 * yalnızca gereksiz bir onay kartı.
 */
export const OP_IMPACTS = ['NONE', 'DECREASE', 'INCREASE', 'NEW_SPEND'] as const;
export type OpImpact = (typeof OP_IMPACTS)[number];

/** Operasyonun dokunduğu nesne türü — gruplama, arayüz ve politika için. */
export const OP_LEVELS = [
  'ACCOUNT',
  'CAMPAIGN',
  'ADSET',
  'AD',
  'CREATIVE',
  'ASSET',
  'MEDIA',
  'KEYWORD',
  'TARGETING',
  'AUDIENCE',
  'CONVERSION',
  'CATALOG',
  'RULE',
  'EXPERIMENT',
  'RECOMMENDATION',
  'BUDGET',
  'BIDDING',
  'LEAD_FORM',
  'POST',
  'IDENTITY',
  'REPORT',
] as const;
export type OpLevel = (typeof OP_LEVELS)[number];

export type OpKind = 'read' | 'write';

export interface OpText {
  en: string;
  tr: string;
}

/**
 * Bütçe hedefi — `INCREASE` bir operasyonun YÜZDE değişimini politika
 * hesaplayabilsin diye. Politika mevcut bütçeyi yerel veritabanından (ya da
 * gerekirse platformdan) okur, oranı `maxBudgetChangePercent` ile karşılaştırır.
 *
 * `ref` platform kimliği YA DA bizim UUID'miz olabilir; çözümleme servisin işi.
 */
export interface OpBudgetTarget {
  level: 'CAMPAIGN' | 'ADSET';
  ref: string;
  /** Yeni günlük bütçe (ANA birim, hesabın para birimi). */
  daily?: number;
  /** Yeni ömür bütçesi (ANA birim). */
  lifetime?: number;
  /**
   * Yeni TEKLİF / hedef maliyet (ANA birim) — teklif artışlarının yüzdesi için.
   * Mevcut teklif yerel kopyada yok; canlı bütçe yoklaması (`opBudgetProbes`)
   * `bid` döndürmeli, döndürmezse politika onaya düşer. (29 Eyl 2026: bu boyut
   * olmadan otomatik pilot hiçbir teklifi ARTIRAMIYORDU.)
   */
  bid?: number;
}

/**
 * Yeni harcama — `NEW_SPEND` bir operasyonun açtığı GÜNLÜK harcama tavanı
 * (ANA birim). `maxDailySpendIncrease` ve günlük yeni kampanya tavanı için.
 * Bilinmiyorsa `null` → politika onaya düşürür.
 */
export interface OpNewSpend {
  dailyEquivalent: number | null;
  /** Oluşturulan varlık AKTİF mi doğuyor (true) yoksa PAUSED mı (false). */
  activates: boolean;
}

/**
 * Taşınan para (Faz 7, 30 Eyl 2026) — `FUNDS` hassasiyetli bir operasyonun
 * TEK çağrıda taşıdığı tutar. Otomatik pilotun günlük para hareketi tavanı
 * (`maxFundsMovedPerDay`) için.
 *
 *   amount    ANA birim (10.50 = on buçuk; kuruş/mikro DEĞİL), ≥ 0
 *   currency  ISO 4217 — tutarın GERÇEK para birimi. 🔴 Model yazdıysa
 *             işleyici platformdaki birimle karşılaştırıp uyuşmazlıkta
 *             REDDETMELİ: "900 TRY" diye onaylanan hareketin 900 USD
 *             taşıması tavanı anlamsız kılar.
 *
 * Tutar parametreden okunamıyorsa (ör. "tüm bakiyeyi taşı", tahsisi silme)
 * `null` döndür → politika onaya düşer (`FUNDS_UNKNOWN`).
 */
export interface OpFundsAmount {
  amount: number;
  currency: string;
}

/**
 * Tek bir operasyonun tanımı.
 *
 * `id` biçimi: `<platform>.<nesne>.<fiil>` — küçük harf, nokta ve alt çizgi
 * (`google.campaign.create`, `meta.adset.update_targeting`,
 * `google.search_term.add_as_negative`). Platform öneki `PLATFORM_OP_PREFIX`
 * ile eşleşmek ZORUNDA (kayıt testi kontrol ediyor).
 *
 * `params` İÇİNDE `workspaceId`/`adAccountId` YOK: kapsam oturumdan gelir
 * (ajan araçlarının kuralı) ve ürün her platformdan tek reklam hesabına izin
 * verdiği için hesap platformdan çözülür.
 *
 * Varlık referansları (kampanya/ad set/reklam/kelime) platform kimliği YA DA
 * bizim UUID'miz olabilir; işleyici `ctx.resolveRef()` ile çözer. Modelin
 * elindeki kimlik çoğu zaman `get_campaigns`ten gelen UUID'dir.
 */
export interface PlatformOpDef<P extends z.ZodType = z.ZodType> {
  id: string;
  platform: AdPlatform;
  kind: OpKind;
  level: OpLevel;
  /** Arayüz kart başlığı. */
  title: OpText;
  /**
   * MODELE giden açıklama — İNGİLİZCE. Ne yaptığı + NE ZAMAN kullanılacağı +
   * kritik kısıtlar (sınırlar, zorunlu alan kombinasyonları, onay gereksinimi).
   * Araç arama (BM25) bu metin üzerinde çalışıyor: platform adını ve
   * kullanıcının kullanacağı sözcükleri içermeli ("YouTube video ad",
   * "Performance Max", "lookalike audience").
   */
  description: string;
  params: P;
  /** Sabit etki ya da parametreye bağlı etki (ör. durum → ACTIVE ise NEW_SPEND). */
  impact: OpImpact | ((params: z.output<P>) => OpImpact);
  /** Başka bir operasyonla geri alınabilir mi. Silme/kalıcı kaldırma = false. */
  reversible: boolean;
  /** Platform tarafında uygulamadan doğrulama destekleniyor mu (validate_only). */
  dryRun: boolean;
  /** INCREASE operasyonlarında yüzde hesabı için. */
  budgetTarget?: (params: z.output<P>) => OpBudgetTarget | null;
  /** NEW_SPEND operasyonlarında tavan hesabı için. */
  newSpend?: (params: z.output<P>) => OpNewSpend;
  /**
   * FUNDS operasyonlarında taşınan tutar (Faz 7) — günlük para hareketi
   * tavanı için. Yoksa ya da `null` dönerse FUNDS işi otomatik pilotta HER
   * ZAMAN onaya düşer (`FUNDS_UNKNOWN`), sahip anahtarı açmış olsa bile.
   */
  fundsAmount?: (params: z.output<P>) => OpFundsAmount | null;
  /**
   * HASSAS işlem — harcama etkisinden BAĞIMSIZ olarak her zaman insan onayı
   * ister (otomatik pilot dahil) ve REST/MCP'de ADMIN yetkisi gerektirir.
   * Parametreye bağlı olabilir (ör. hesap ayarı yalnız izleme şablonu
   * değişiyorsa DESTINATION).
   *
   *   ACCESS        hesap erişimi/rol verme-alma, üye ekleme
   *   DESTINATION   tıklamanın gittiği yer: nihai URL, izleme şablonu,
   *                 URL son eki, açılış sayfası, kart adresi
   *   DATA_SHARING  kitle/veri başka bir hesaba/işletmeye paylaşma
   *   LEGAL         yasal beyan (AB siyasi reklam beyanı, özel reklam kategorisi)
   *   PUBLISHING    markanın herkese açık kanalında ORGANİK içerik yayınlama
   *                 (sayfa gönderisi) — reklam değil, geri alınması itibar işi
   *   FUNDS         para taşıma (aşağıda `OP_SENSITIVITIES` notu); tutarı
   *                 `fundsAmount` bildirir
   *
   * Faz 7 (30 Eyl 2026): "her zaman onay" artık VARSAYILAN — çalışma alanı
   * SAHİBİ türe göre otomatiğe açabiliyor (`AutonomyPolicy.autoSensitive`,
   * FUNDS için ayrıca günlük tutar tavanı). REST/MCP'deki ADMIN şartı aynen.
   *
   * 29 Eyl 2026 güvenlik denetimi: bu işlemler `NONE` sınıflıydı ve otomatik
   * pilot onaysız uyguluyordu — tek bir enjekte edilmiş platform metni tüm
   * tıklamaları saldırgan adrese yönlendirebilir ya da hesaba yabancı bir
   * yönetici ekleyebilirdi.
   */
  sensitive?: OpSensitivity | ((params: z.output<P>) => OpSensitivity | null);
  /**
   * Platformun istediği erişim — dürüst hata mesajı ve yetenek ekranı için.
   * Kod "onay alınmış gibi" çalışır; platform reddederse hata bu notu taşır.
   */
  requires?: {
    /** "Standard access", "Advanced Access: ads_management", "allowlist: operation_status"… */
    access?: string;
    scopes?: readonly string[];
    /**
     * MÜŞTERİ METNİ — modele, karta, REST/MCP kataloğuna giden not. Sunucu
     * bayrağı (`*_ENABLED=true`), `.env`, "API'yi yeniden başlat" gibi
     * GELİŞTİRİCİ talimatı TAŞIMAZ (`customer-text.spec.ts` tarıyor): müşteri
     * onu yapamaz, model de "geliştiricine .env'e şunu yazdır" demeye başlar.
     */
    note?: string;
    /**
     * GELİŞTİRİCİ (Mentorivo işletimi) METNİ — hangi bayrak/yapılandırma bu
     * işlemi açar. Yalnız LOGA ve iç araçlara (backstage); müşteriye giden her
     * yüzey `customerRequires()` ile bu alanı DÜŞÜRÜR (Faz 8 D, 30 Eyl 2026).
     */
    operatorNote?: string;
  };
}

/** Müşteriye giden `requires` — geliştirici notu düşürülmüş (yoksa `null`). */
export function customerRequires(
  requires: PlatformOpDef<z.ZodType>['requires'] | null | undefined,
): Omit<NonNullable<PlatformOpDef<z.ZodType>['requires']>, 'operatorNote'> | null {
  if (!requires) return null;
  const { operatorNote: _operator, ...customer } = requires;
  void _operator;
  return customer;
}

/**
 * Müşteri metninde OLMAMASI gereken geliştirici kalıpları — sunucu bayrağı
 * ataması (`X_ENABLED=true`), `.env`, API/worker yeniden başlatma, uygulama
 * yapılandırma kimliği (`META_CONFIG_ID`). Test ve savunma süzgeci kullanır.
 */
export const OPERATOR_TEXT_PATTERN =
  /\b(?:META|GOOGLE|LINKEDIN|TWITTER|TIKTOK|X)_[A-Z0-9_]*(?:ENABLED|SANDBOX|CONFIG_ID|PRODUCT_[A-Z0-9_]+)\b|\b[A-Z][A-Z0-9]*_[A-Z0-9_]+=(?:true|false)\b|\.env\b|\b(?:restart|yeniden başlat)\w*\s+(?:the\s+)?(?:API|worker)\b/;

/**
 * `FUNDS` (Faz 7, 30 Eyl 2026): para TAŞIYAN işlemler — İşletme Merkezi bakiye
 * transferi, kredi hattını başka işletmeye/portföye tahsis. Harcama değil
 * (etkisi `NONE` olabilir) ama geri dönüşü para hareketi; en ağır hassasiyet.
 */
export const OP_SENSITIVITIES = ['ACCESS', 'DESTINATION', 'DATA_SHARING', 'LEGAL', 'PUBLISHING', 'FUNDS'] as const;
export type OpSensitivity = (typeof OP_SENSITIVITIES)[number];

/** Kayıt defterinde tutulan, tip parametresi silinmiş hâl. */
export type AnyPlatformOpDef = PlatformOpDef<z.ZodType>;

/** Platform → operasyon kimliği öneki. */
export const PLATFORM_OP_PREFIX: Readonly<Partial<Record<AdPlatform, string>>> = {
  GOOGLE: 'google.',
  META: 'meta.',
  TIKTOK: 'tiktok.',
  LINKEDIN: 'linkedin.',
  TWITTER: 'x.',
};

export const OP_ID_PATTERN = /^(google|meta|tiktok|linkedin|x)\.[a-z0-9_]+(\.[a-z0-9_]+)+$/;

/**
 * Tanım yardımcısı — tip çıkarımı için (`params` şeması `impact`/`budgetTarget`
 * fonksiyonlarının parametre tipini belirlesin).
 */
export function defineOp<P extends z.ZodType>(def: PlatformOpDef<P>): PlatformOpDef<P> {
  return def;
}

/** Hassasiyeti parametreyle çözer (`null` = hassas değil). */
export function resolveSensitivity(def: AnyPlatformOpDef, params: unknown): OpSensitivity | null {
  if (!def.sensitive) return null;
  return typeof def.sensitive === 'function' ? def.sensitive(params) : def.sensitive;
}

/** Etkiyi parametreyle çözer. */
export function resolveImpact(def: AnyPlatformOpDef, params: unknown): OpImpact {
  return typeof def.impact === 'function' ? def.impact(params) : def.impact;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sonuç — işleyicinin döndürdüğü
// ═══════════════════════════════════════════════════════════════════════════

export interface OpEntityRef {
  level: OpLevel;
  platformRef: string;
  name?: string;
  /** Platformun bildirdiği durum (ör. PAUSED). */
  status?: string;
}

export interface OpChange {
  level: OpLevel;
  platformRef: string;
  before?: unknown;
  after?: unknown;
}

export interface OpResult {
  /**
   * İNGİLİZCE, 1–3 cümle: ne oldu. Modele ve denetim kaydına gider. Ham
   * platform yanıtı DEĞİL; jeton/sır taşımaz.
   */
  summary: string;
  /** Okuma sonucu ya da oluşturulan nesnenin ayrıntısı. Servis boyutu kırpar. */
  data?: unknown;
  created?: OpEntityRef[];
  changed?: OpChange[];
  /** Başarılı ama dikkat gerektiren durumlar (ör. "reklam incelemede"). */
  warnings?: string[];
  /** `dryRun` ile çalıştı: platform doğruladı, hiçbir şey uygulanmadı. */
  validatedOnly?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ortak parametre parçaları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Varlık referansı: platform kimliği (`123`, `customers/1/campaigns/2`,
 * `act_1`) YA DA bizim UUID'miz. Çözüm işleyicide `ctx.resolveRef`.
 */
export const opEntityRef = z
  .string()
  .trim()
  .min(1)
  .max(300)
  .describe(
    'Entity id: the platform-native id/resource name OR the Mentorivo UUID returned by get_campaigns.',
  );

/** Mentorivo kreatif kütüphanesindeki bir kreatifin kimliği (UUID). */
export const opCreativeId = z
  .uuid()
  .describe('Mentorivo creative library id (from list_creatives). Its image/video is uploaded to the platform.');

/** ANA birim para tutarı (hesabın para birimi). */
export const opMoney = z.number().positive().max(100_000_000);

/** ISO 8601 an. */
export const opDateTime = z.iso.datetime({ offset: true });

/** ISO 3166-1 alfa-2. */
export const opCountry = z
  .string()
  .trim()
  .length(2)
  .transform((value) => value.toUpperCase());

/**
 * Durum hedefi — etkiyi belirleyen alan. `ACTIVE` NEW_SPEND/INCREASE,
 * `PAUSED` DECREASE sayılır (bkz. `statusImpact`).
 */
export const opTargetStatus = z.enum(['ACTIVE', 'PAUSED']);

export function statusImpact(status: 'ACTIVE' | 'PAUSED' | undefined): OpImpact {
  return status === 'ACTIVE' ? 'NEW_SPEND' : 'NONE';
}
